import { ref, computed } from 'vue';
import BibleSearchAPI from 'dashboard/api/bibleSearch';

const STORAGE_KEY_RECENT = 'bible_recent';
const STORAGE_KEY_FAVORITES = 'bible_favorites';
const MAX_RECENT = 10;

export const LANGUAGES = [
  {
    code: 'en',
    label: 'English',
    translations: [
      { id: 'KJV', label: 'KJV — King James Version' },
      { id: 'ASV', label: 'ASV — American Standard' },
      { id: 'WEB', label: 'WEB — World English Bible' },
    ],
  },
  {
    code: 'es',
    label: 'Spanish',
    translations: [{ id: 'RVR1960', label: 'RVR1960 — Reina Valera' }],
  },
  {
    code: 'ar',
    label: 'Arabic',
    translations: [{ id: 'SVD', label: 'SVD — Smith-Van Dyke' }],
  },
  {
    code: 'fr',
    label: 'French',
    translations: [{ id: 'LSG', label: 'LSG — Louis Segond' }],
  },
  {
    code: 'pt',
    label: 'Portuguese',
    translations: [{ id: 'ARA', label: 'ARA — Almeida Revisada' }],
  },
  {
    code: 'ur',
    label: 'Urdu',
    translations: [{ id: 'URD', label: 'Urdu Bible' }],
  },
  {
    code: 'sw',
    label: 'Swahili',
    translations: [{ id: 'SWA', label: 'Swahili Bible' }],
  },
  {
    code: 'hi',
    label: 'Hindi',
    translations: [{ id: 'HIN', label: 'Hindi Bible' }],
  },
  {
    code: 'zh',
    label: 'Mandarin',
    translations: [{ id: 'CNVS', label: 'CNVS — Chinese New Version' }],
  },
  {
    code: 'ru',
    label: 'Russian',
    translations: [{ id: 'RUSV', label: 'RUSV — Russian Synodal' }],
  },
];

function loadFromStorage(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || '[]');
  } catch {
    return [];
  }
}

function saveToStorage(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // localStorage unavailable
  }
}

// Detect if query looks like a reference (e.g. "John 3:16", "Romans 8")
function isReference(query) {
  return (
    /^[1-3]?\s*[a-zA-Z]+\.?\s+\d+:\d+/.test(query.trim()) ||
    /^[1-3]?\s*[a-zA-Z]+\.?\s+\d+$/.test(query.trim())
  );
}

export function useBibleSearch() {
  const query = ref('');
  const selectedLanguage = ref(LANGUAGES[0]);
  const selectedTranslation = ref(LANGUAGES[0].translations[0]);
  const results = ref([]);
  const isLoading = ref(false);
  const error = ref(null);
  const recent = ref(loadFromStorage(STORAGE_KEY_RECENT));
  const favorites = ref(loadFromStorage(STORAGE_KEY_FAVORITES));

  const availableTranslations = computed(
    () => selectedLanguage.value.translations
  );

  function selectLanguage(lang) {
    selectedLanguage.value = lang;
    selectedTranslation.value = lang.translations[0];
  }

  async function search() {
    if (!query.value.trim()) return;

    isLoading.value = true;
    error.value = null;
    results.value = [];

    try {
      let response;
      if (isReference(query.value)) {
        response = await BibleSearchAPI.verse({
          ref: query.value.trim(),
          translation: selectedTranslation.value.id.toLowerCase(),
        });
      } else {
        response = await BibleSearchAPI.search({
          query: query.value.trim(),
          translation: selectedTranslation.value.id,
        });
      }
      results.value = response.data.verses || [];
      if (results.value.length === 0) {
        error.value = 'No verses found. Try a different keyword or reference.';
      }
    } catch (e) {
      const msg = e?.response?.data?.error;
      error.value = msg || 'Search failed. Check your connection or API key.';
    } finally {
      isLoading.value = false;
    }
  }

  function formatVerse(verse) {
    return `📖 ${verse.reference} (${verse.translation})\n"${verse.text}"`;
  }

  function trackRecent(verse) {
    const formatted = formatVerse(verse);
    const entry = { ...verse, formatted, savedAt: Date.now() };
    const updated = [
      entry,
      ...recent.value.filter(
        r =>
          r.reference !== verse.reference || r.translation !== verse.translation
      ),
    ].slice(0, MAX_RECENT);
    recent.value = updated;
    saveToStorage(STORAGE_KEY_RECENT, updated);
  }

  function isFavorited(verse) {
    return favorites.value.some(
      f =>
        f.reference === verse.reference && f.translation === verse.translation
    );
  }

  function toggleFavorite(verse) {
    const formatted = formatVerse(verse);
    if (isFavorited(verse)) {
      favorites.value = favorites.value.filter(
        f =>
          !(
            f.reference === verse.reference &&
            f.translation === verse.translation
          )
      );
    } else {
      favorites.value = [
        { ...verse, formatted, savedAt: Date.now() },
        ...favorites.value,
      ];
    }
    saveToStorage(STORAGE_KEY_FAVORITES, favorites.value);
  }

  function clearRecent() {
    recent.value = [];
    saveToStorage(STORAGE_KEY_RECENT, []);
  }

  return {
    query,
    selectedLanguage,
    selectedTranslation,
    availableTranslations,
    results,
    isLoading,
    error,
    recent,
    favorites,
    LANGUAGES,
    selectLanguage,
    search,
    formatVerse,
    trackRecent,
    isFavorited,
    toggleFavorite,
    clearRecent,
  };
}
